import React, { createContext, useContext, useState, ReactNode } from 'react';

// Definir la interfaz para los datos de la empresa
export interface CompanyData {
  category?: string;
  name?: string;
  lastname?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  services?: string[];
  profileImage?: string | null;
  documents?: Array<{
    name: string;
    type: string;
    size: number;
    category?: string;
    file?: File;
    preview?: string;
    url?: string; // URL de Firebase Storage
  }>;
  additionalImages?: Array<{
    name: string;
    preview: string;
    size: number;
  }>;
  legalRepName?: string;
  legalRepId?: string;
  employeeCount?: number;
  yearsExperience?: number;
  companyName?: string;
  companyEmail?: string;
  companyPhone?: string;
  companyAddress?: string;
  companyDescription?: string;
  serviceImages?: string[];
  locations?: string[];
  leadPrice?: number;
  termsAccepted?: boolean;
  // Podemos agregar más campos según sea necesario para las siguientes páginas
}

// Interfaz para el contexto
interface OnboardingContextType {
  companyData: CompanyData;
  updateCompanyData: (data: Partial<CompanyData>) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  totalSteps: number;
  resetOnboarding: () => void;
}

// Crear el contexto
const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// Props para el proveedor
interface OnboardingProviderProps {
  children: ReactNode;
}

// Proveedor del contexto
export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const [companyData, setCompanyData] = useState<CompanyData>({});
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 8; // Total de páginas en el onboarding (añadido paso para precio de leads)

  // Función para actualizar los datos de la empresa
  const updateCompanyData = (data: Partial<CompanyData>) => {
    setCompanyData(prevData => ({
      ...prevData,
      ...data
    }));
  };

  // Función para resetear todo el onboarding
  const resetOnboarding = () => {
    // Limpiar datos del contexto
    setCompanyData({});
    setCurrentStep(1);
    
    // Limpiar localStorage relacionado con el onboarding
    localStorage.removeItem('selectedCategoryId');
    
    // Limpiar cualquier otro dato en cache relacionado con onboarding
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('onboarding_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  return (
    <OnboardingContext.Provider value={{
      companyData,
      updateCompanyData,
      currentStep,
      setCurrentStep,
      totalSteps,
      resetOnboarding
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding debe ser usado dentro de un OnboardingProvider');
  }
  return context;
};
